import { Controller } from "@hotwired/stimulus"
import { startKeyboardOffset } from "../utils/keyboard_offset"

export default class extends Controller {
  static targets = [
    "overlay", "modal", "input", "results", "item",
    "defaultContent", "searchResults",
    "recentSection", "recentList", "actionsSection", "actionItem"
  ]
  static values = {
    searchUrl: String,
    noResultsText: { type: String, default: "No results for \"%{query}\"" },
    actionsText: { type: String, default: "Actions" },
    entriesText: { type: String, default: "Entries" },
    draftText: { type: String, default: "Draft" },
    scheduledText: { type: String, default: "Scheduled" }
  }

  connect() {
    this.selectedIndex = -1
    this.searchTimeout = null
    this.stopKeyboardOffset = null
    this.recentLoaded = false
  }

  disconnect() {
    if (this.stopKeyboardOffset) {
      this.stopKeyboardOffset()
      this.stopKeyboardOffset = null
    }
  }

  // Open the command palette
  open() {
    this.overlayTarget.classList.add("open")
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.showDefault()
    this.selectedIndex = -1
    document.body.style.overflow = "hidden"
    this.stopKeyboardOffset = startKeyboardOffset(this.overlayTarget)
    this.loadRecent()
  }

  // Close the command palette
  close() {
    if (this.stopKeyboardOffset) {
      this.stopKeyboardOffset()
      this.stopKeyboardOffset = null
    }
    this.overlayTarget.classList.remove("open")
    document.body.style.overflow = ""
    this.clearSearch()
  }

  // Handle global keyboard shortcuts
  handleKeydown(event) {
    // ⌘K or Ctrl+K — open palette
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      if (this.overlayTarget.classList.contains("open")) {
        this.close()
      } else {
        this.open()
      }
      return
    }

    // ⌘N — new entry
    if ((event.metaKey || event.ctrlKey) && event.key === "n" && !this.isEditing()) {
      event.preventDefault()
      window.location.href = this.overlayTarget.querySelector('[href*="/new"]')?.href
      return
    }

    // ⌘, — settings
    if ((event.metaKey || event.ctrlKey) && event.key === ",") {
      event.preventDefault()
      window.location.href = this.overlayTarget.querySelector('[href*="/settings"]')?.href
      return
    }

    // "/" — open palette (when not editing)
    if (event.key === "/" && !this.isEditing() && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault()
      this.open()
      return
    }

    // Escape — close
    if (event.key === "Escape" && this.overlayTarget.classList.contains("open")) {
      event.preventDefault()
      this.close()
    }
  }

  // Click on overlay to close
  clickOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  // Navigate items with arrow keys
  navigate(event) {
    const items = this.getVisibleItems()
    if (!items.length) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.updateSelection(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection(items)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          items[this.selectedIndex].click()
          this.close()
        }
        break
    }
  }

  // Search entries
  search() {
    const query = this.inputTarget.value.trim()

    if (!query) {
      this.showDefault()
      return
    }

    // Filter actions client-side (instant)
    const filteredActions = this.filterActions(query)

    // Show search results container with actions immediately
    this.defaultContentTarget.style.display = "none"
    this.searchResultsTarget.style.display = "block"
    this.renderCombinedResults(filteredActions, null, query)

    // Debounced server search for entries
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this.performSearch(query, filteredActions), 200)
  }

  filterActions(query) {
    const q = query.toLowerCase()
    // Only query from actionsSection to avoid cloned items in searchResults
    const originals = this.actionsSectionTarget.querySelectorAll('[data-command-palette-target~="actionItem"]')
    return Array.from(originals).filter(item => {
      const label = (item.dataset.filterLabel || "").toLowerCase()
      const text = (item.querySelector(".command-palette-item-label")?.textContent || "").toLowerCase()
      return label.includes(q) || text.includes(q)
    })
  }

  async performSearch(query, filteredActions) {
    if (!this.searchUrlValue) return

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) return

      // Check if query still matches (user may have typed more)
      if (this.inputTarget.value.trim() !== query) return

      const entries = await response.json()
      this.renderCombinedResults(filteredActions || this.filterActions(query), entries, query)
    } catch (error) {
      console.error("Command palette search failed:", error)
    }
  }

  renderCombinedResults(filteredActions, entries, query) {
    let html = ""

    // Actions section
    if (filteredActions.length > 0) {
      html += `<div class="command-palette-group-title">${this.escapeHtml(this.actionsTextValue)}</div>`
      html += filteredActions.map(item => item.outerHTML).join("")
    }

    // Entries section
    if (entries !== null) {
      if (entries.length > 0) {
        html += `<div class="command-palette-group-title">${this.escapeHtml(this.entriesTextValue)}</div>`
        html += entries.map(entry => this.renderEntryItem(entry)).join("")
      } else if (filteredActions.length === 0) {
        const msg = this.noResultsTextValue.replace("%{query}", this.escapeHtml(query))
        html = `<div class="command-palette-empty">${msg}</div>`
      }
    }

    this.searchResultsTarget.innerHTML = html
    this.selectedIndex = -1
  }

  renderEntryItem(entry) {
    let badge = ""
    if (entry.draft) {
      badge = `<span class="command-palette-badge">${this.escapeHtml(this.draftTextValue)}</span>`
    } else if (entry.scheduled) {
      badge = `<span class="command-palette-badge">${this.escapeHtml(this.scheduledTextValue)}</span>`
    } else if (entry.category) {
      badge = `<span class="command-palette-item-meta">${this.escapeHtml(entry.category)}</span>`
    }

    return `
      <a href="/dashboard/entries/${entry.id}/edit"
         class="command-palette-item"
         data-command-palette-target="item">
        <div class="command-palette-item-left">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/></svg>
          <span class="command-palette-item-label">${this.escapeHtml(entry.title)}</span>
        </div>
        ${badge}
      </a>
    `
  }

  async loadRecent() {
    if (this.recentLoaded || !this.searchUrlValue) return

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) return

      const entries = await response.json()
      this.recentLoaded = true

      if (entries.length > 0) {
        this.recentListTarget.innerHTML = entries.map(entry => this.renderEntryItem(entry)).join("")
        this.recentSectionTarget.style.display = "block"
      }
    } catch (error) {
      // Silently fail — recent section stays hidden
    }
  }

  showDefault() {
    this.defaultContentTarget.style.display = "block"
    this.searchResultsTarget.style.display = "none"
    this.searchResultsTarget.innerHTML = ""
    this.selectedIndex = -1
    // Restore all action item visibility
    this.actionItemTargets.forEach(item => item.style.display = "")
  }

  clearSearch() {
    clearTimeout(this.searchTimeout)
    this.inputTarget.value = ""
    this.showDefault()
  }

  getVisibleItems() {
    return Array.from(this.resultsTarget.querySelectorAll(":scope > :not([style*='display: none']) .command-palette-item"))
  }

  updateSelection(items) {
    items.forEach((item, index) => {
      item.classList.toggle("selected", index === this.selectedIndex)
    })
    if (this.selectedIndex >= 0) {
      items[this.selectedIndex]?.scrollIntoView({ block: "nearest" })
    }
  }

  isEditing() {
    const active = document.activeElement
    return active && (
      active.tagName === "INPUT" ||
      active.tagName === "TEXTAREA" ||
      active.contentEditable === "true"
    )
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
